// This is a modal for Referralstwo

import { Button as AIMEButton } from "tot-loft-component-library";

import * as PRTNR from "./prtnr";

import styled from "@emotion/styled";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { fetchReferralHistoryItems } from "./redux/slices/ReferralHistoryThunk";
import { updateReferral } from "./redux/slices/ReferralsThunks";
import { formatDate2 } from "./utils";
import { InfoCircleIcon } from "./assets";

const statusOptions = [
  { value: "Lead Identified", label: "Lead Identified" },
  { value: "Opportunity", label: "Opportunity" },
  { value: "Proposal Sent", label: "Proposal Sent" },
  { value: "Verbally Committed", label: "Verbally Committed" },
  { value: "Won", label: "Won" },
  { value: "Lost", label: "Lost" },
];

export default function ModalReferralstwo({ onClose, referral, setNotes }) {
  const dispatch = useDispatch();
  const [formValues, setFormValues] = useState({
    status: referral.referral_status,
    notes: referral.notes,
    closeDate: "",
    lostReason: referral?.referral_lost_reason || "",
  });
  const [lostReasonError, setLostReasonError] = useState(false);
  const [closeDate, setCloseDate] = useState(referral?.expected_close_date);
  const [closeDateError, setCloseDateError] = useState(false);

  const currentStatusIndex = statusOptions.findIndex(
    (option) => option.value === referral.referral_status
  );

  const statusOptionsWithDisabled = statusOptions.map((option, index) => {
    const isLostDisabled =
      referral.referral_status === "Won" && option.value === "Lost";

    const isPreviousStepDisabled =
      index < currentStatusIndex && option.value !== "Lost";

    return {
      ...option,
      isDisabled: isLostDisabled || isPreviousStepDisabled,
    };
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "lostReason" && value) {
      setLostReasonError(false);
    }

    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleUpdate = () => {
    const { status, notes, lostReason } = formValues;
    const isStatusFinal = status === "Won" || status === "Lost";

    if (status === "Lost" && !lostReason) {
      setLostReasonError(true);
      return;
    } else {
      setLostReasonError(false);
    }
    if (!isStatusFinal && !closeDate) {
      setCloseDateError("Expected Close Date is Required");
      return;
    }

    setCloseDateError(false);

    const statusUnchanged = referral?.referral_status === status;
    const notesUnchanged = (referral?.notes || "") === notes;
    const lostReasonUnchanged =
      (referral?.referral_lost_reason || "") === (lostReason || "");
    const closeDateUnchanged =
      (referral?.expected_close_date || "") === (closeDate || "");

    if (
      statusUnchanged &&
      notesUnchanged &&
      lostReasonUnchanged &&
      closeDateUnchanged
    ) {
      onClose();
      return;
    }

    const date = Date.now();
    const payload = {
      _id: referral._id,
      referral_status: status,
      notes,
      referral_lost_reason: lostReason,
      expected_close_date: closeDate,
      updated_at: date,
    };
    if (status === "Lost") {
      payload.referral_member_status = "Unsuccessful";
    }

    dispatch(updateReferral(payload));
    if (typeof setNotes === "function") {
      setNotes(notes);
    }
    dispatch(fetchReferralHistoryItems(referral._id));
    onClose();
  };

  return (
    <RootWrapperModalReferralstwo>
      <Overlay>
        <Wrapper>
          <StatusUpdate>
            <Left>
              <Frame58>
                <Frame1391>
                  <Frame1527>
                    <Frame1527_0001>
                      <ChangeStatus>Change Status</ChangeStatus>
                    </Frame1527_0001>
                  </Frame1527>
                  <PleaseUpdateTheStatusOfThisReferralBasedOnCurrentInformation>
                    Please update the status of this referral based on current
                    information.
                  </PleaseUpdateTheStatusOfThisReferralBasedOnCurrentInformation>
                </Frame1391>
                <Frame1391>
                  <PRTNR.Select
                    label="Status"
                    name="status"
                    value={formValues.status}
                    onChange={handleChange}
                    options={statusOptionsWithDisabled}
                  />
                  {formValues.status === "Lost" && (
                    <PRTNR.Select
                      name="lostReason"
                      label="Lost Reason"
                      value={formValues.lostReason}
                      onChange={handleChange}
                      options={[
                        { value: "No Fit", label: "No Fit" },
                        { value: "No Budget", label: "No Budget" },
                        { value: "No Authority", label: "No Authority" },
                        {
                          value: "No Timeline / Not Ready",
                          label: "No Timeline / Not Ready",
                        },
                        { value: "Unresponsive", label: "Unresponsive" },
                        { value: "Deal Cancelled", label: "Deal Cancelled" },
                        {
                          value: "Lost to Competitor",
                          label: "Lost to Competitor",
                        },
                      ]}
                      required
                      error={lostReasonError}
                      errorMessage={"Lost reason is required"}
                    />
                  )}

                  {formValues.status !== "Won" &&
                    formValues.status !== "Lost" && (
                      <PRTNR.Input
                        label="Expected Close Date"
                        type="date"
                        min={new Date().toISOString().split("T")[0]}
                        value={formatDate2(closeDate)}
                        onChange={(e) => {
                          setCloseDate(e.target.value);
                          setCloseDateError(false);
                        }}
                        required
                        error={!!closeDateError}
                        errorMessage={closeDateError}
                      />
                    )}
                  <PRTNR.TextArea
                    name="notes"
                    label={
                      <LabelWrapper>
                        <span>Notes</span>
                        <TooltipWrapper>
                          <GFeildsInfo src={InfoCircleIcon} alt="icon" />
                          <TooltipText className="tooltip">
                            Use this field to note internal updates, actions, or
                            context on your referral’s progress. Visible only to
                            your team
                          </TooltipText>
                        </TooltipWrapper>
                      </LabelWrapper>
                    }
                    placeholder="Write notes here..."
                    value={formValues.notes}
                    onChange={handleChange}
                  />
                </Frame1391>
                <Frame59>
                  <AIMEButton
                    size="small"
                    onClick={() => onClose()}
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      flexDirection: "row",
                      alignItems: "center",
                      flex: "none",
                      borderRadius: "999px",
                      background: "rgb(241, 245, 249)",
                      backgroundRepeat: "no-repeat",
                      backgroundSize: "contain",
                      backgroundPosition: "center",
                      boxSizing: "border-box",
                      padding: "16px 32px",
                      "@media (max-width: 1024px)": {
                        width: "240px",
                      },
                      "@media (max-width: 767px)": {
                        width: "100%",
                      },
                    }}
                  >
                    <span
                      style={{
                        color: "rgb(33, 37, 67)",
                        textOverflow: "ellipsis",
                        fontSize: "16px",
                        fontFamily: "Montserrat, sans-serif",
                        fontWeight: "600",
                        lineHeight: "110%",
                        textAlign: "left",
                      }}
                    >
                      Cancel
                    </span>
                  </AIMEButton>

                  <AIMEButton
                    size="small"
                    onClick={() => handleUpdate()}
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      flexDirection: "row",
                      alignItems: "center",
                      flex: "none",
                      gap: "10px",
                      borderRadius: "48px",
                      width: "240px",
                      background: "rgb(97, 98, 197)",
                      backgroundRepeat: "no-repeat",
                      backgroundSize: "contain",
                      backgroundPosition: "center",
                      boxSizing: "border-box",
                      padding: "16px 32px",
                      "@media (max-width: 1024px)": {
                        width: "240px",
                      },
                      "@media (max-width: 767px)": {
                        width: "100%",
                      },
                    }}
                  >
                    <span
                      style={{
                        color: "white",
                        textOverflow: "ellipsis",
                        fontSize: "16px",
                        fontFamily: "Montserrat, sans-serif",
                        fontWeight: "600",
                        lineHeight: "110%",
                        textAlign: "left",
                      }}
                    >
                      Update
                    </span>
                  </AIMEButton>
                </Frame59>
              </Frame58>
            </Left>
          </StatusUpdate>
        </Wrapper>
      </Overlay>
    </RootWrapperModalReferralstwo>
  );
}

const LabelWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 4px;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const GFeildsInfo = styled.img`
  width: 18px;
  height: 18px;
  object-fit: cover;
`;

const TooltipWrapper = styled.div`
  position: relative;
  display: inline-block;

  &:hover .tooltip {
    visibility: visible;
    opacity: 1;
  }
`;

const TooltipText = styled.div`
  visibility: hidden;
  color: #000000;
  font-weight: 500;
  font-size: 12px;
  text-align: start;
  padding: 8px 16px;
  border-radius: 4px;
  position: absolute;
  z-index: 99999;
  top: 125%; /* Show below the icon */
  left: 0;
  transform: translateX(0);
  opacity: 0;
  transition: opacity 0.3s;
  max-width: 280px;
  width: 280px;
  backdrop-filter: blur(20px);
  background: var(--backgrounds-primary, #ffffff);
  font-family: "Montserrat", sans-serif;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  &::after {
    content: "";
    position: absolute;
    bottom: 100%; /* Top of the tooltip box */
    left: 16px;
    margin-left: 0px;
    border-width: 6px;
    border-style: solid;
    border-color: transparent transparent var(--backgrounds-primary, #ffffff)
      transparent;
  }
`;

const RootWrapperModalReferralstwo = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
`;

const Overlay = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  flex: none;
  gap: 8px;
  box-sizing: border-box;
`;

const Wrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  flex: none;
  border-radius: 32px;
  background: rgb(255, 255, 255);
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  box-sizing: border-box;
  padding: 40px 24px 24px;
`;

const StatusUpdate = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: flex-start;
  flex: none;
  gap: 24px;
  width: 610px;
  box-sizing: border-box;
  @media (max-width: 1024px) and (min-width: 768px) {
    width: 100%;
  }

  @media (max-width: 767px) {
    width: 100%;
  }
`;

const Left = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  gap: 8px;
  width: 610px;
  box-sizing: border-box;
  @media (max-width: 1024px) and (min-width: 768px) {
    width: 100%;
  }

  @media (max-width: 767px) {
    width: 100%;
  }
`;

const Frame58 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const Frame1391 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const Frame1527 = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  align-items: center;
  align-self: stretch;
  flex-shrink: 0;
`;

const Frame1527_0001 = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  width: 610px;
  box-sizing: border-box;
  @media (max-width: 1024px) and (min-width: 768px) {
    width: 100%;
  }

  @media (max-width: 767px) {
    width: 100%;
  }
`;

const ChangeStatus = styled.span`
  color: rgb(33, 37, 67);
  text-overflow: ellipsis;
  font-size: 30px;
  font-family: Montserrat, sans-serif;
  font-weight: 700;
  text-align: left;
  width: 612px;
  flex: 1;
  @media (max-width: 1024px) and (min-width: 768px) {
    width: 100%;
  }

  @media (max-width: 767px) {
    width: 100%;
  }
`;

const PleaseUpdateTheStatusOfThisReferralBasedOnCurrentInformation = styled.span`
  color: rgb(33, 37, 67);
  text-overflow: ellipsis;
  font-size: 14px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: left;
  align-self: stretch;
  flex-shrink: 0;
`;

const Frame59 = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: row;
  align-items: flex-start;
  align-self: stretch;
  box-sizing: border-box;
  flex-shrink: 0;
  gap: 16px;
  @media (max-width: 1024px) and (min-width: 768px) {
    width: 100%;
  }

  @media (max-width: 767px) {
    flex-direction: column-reverse;
  }
`;
